import {
  useCollaborationHighlightStore,
  type CollaborationHighlightContext,
  type CollaborationHighlightTargetType,
} from '@/stores/collaborationHighlightStore';

export type ResourceLibraryHighlightTab = 'character' | 'location' | 'prop' | 'footage';

export const RESOURCE_LIBRARY_SCOPE = 'resource-library';

export const RESOURCE_LIBRARY_HIGHLIGHT_TARGET_TYPES = [
  'resource_element',
  'resource_tag',
  'project_resource',
] as const satisfies readonly CollaborationHighlightTargetType[];

export const RESOURCE_TAB_ELEMENT_TYPES: Record<
  ResourceLibraryHighlightTab,
  readonly string[]
> = {
  character: ['character', '1'],
  location: ['location', '2'],
  prop: ['prop', '3'],
  footage: ['footage', '4'],
};

export function getResourceTypeHighlightScope(type: string): string {
  return `resource-type:${type}`;
}

export function getResourceTargetHighlightScope(resourceId: string): string {
  return `resource-target:${resourceId}`;
}

type ResourceLibraryHighlightRoute = {
  projectId: string;
  parentProjectId?: string | null;
  context: CollaborationHighlightContext;
};

function hasRemainingResourceCategoryHighlights(
  route: ResourceLibraryHighlightRoute,
  category: ResourceLibraryHighlightTab
): boolean {
  return useCollaborationHighlightStore.getState().hasAnyHighlight({
    projectId: route.projectId,
    parentProjectId: route.parentProjectId,
    context: route.context,
    targetTypes: RESOURCE_LIBRARY_HIGHLIGHT_TARGET_TYPES,
    elementTypes: RESOURCE_TAB_ELEMENT_TYPES[category],
  });
}

function hasRemainingResourceLibraryHighlights(route: ResourceLibraryHighlightRoute): boolean {
  return useCollaborationHighlightStore.getState().hasAnyHighlight({
    projectId: route.projectId,
    parentProjectId: route.parentProjectId,
    context: route.context,
    targetTypes: RESOURCE_LIBRARY_HIGHLIGHT_TARGET_TYPES,
  });
}

/** 点击资源卡片：移除该条高亮，并在同类/全库无剩余时向上 dismiss Tab 红点。 */
export function acknowledgeResourceLibraryTargetHighlight(params: {
  route: ResourceLibraryHighlightRoute;
  resourceId: string;
  category: ResourceLibraryHighlightTab;
  dismissRouteScope: (scope: string) => void;
}): void {
  const store = useCollaborationHighlightStore.getState();
  const { route, resourceId, category, dismissRouteScope } = params;

  for (const targetType of RESOURCE_LIBRARY_HIGHLIGHT_TARGET_TYPES) {
    store.clearHighlight({
      projectId: route.projectId,
      parentProjectId: route.parentProjectId,
      context: route.context,
      targetType,
      targetId: resourceId,
    });
  }

  if (!hasRemainingResourceCategoryHighlights(route, category)) {
    dismissRouteScope(getResourceTypeHighlightScope(category));
  }

  if (!hasRemainingResourceLibraryHighlights(route)) {
    dismissRouteScope(RESOURCE_LIBRARY_SCOPE);
  }
}

/** 进入资源分类 Tab：清除该分类与左侧「资源库」Tab 红点（不影响其他分类）。 */
export function acknowledgeResourceLibraryCategoryTab(params: {
  category: ResourceLibraryHighlightTab;
  dismissRouteScope: (scope: string) => void;
}): void {
  const { category, dismissRouteScope } = params;
  dismissRouteScope(getResourceTypeHighlightScope(category));
  dismissRouteScope(RESOURCE_LIBRARY_SCOPE);
}
